import api from './api';

/**
 * Buying Cognitive Flows credits, in one place.
 *
 * Lifted out of CognitiveFlowsContext (2026-08-21) so the Billing & Payments
 * settings tab can offer the SAME purchase as the Cognitive Flows dashboard.
 * The settings page lives outside CognitiveFlowsProvider — it talks to the
 * feature through the module-level axios instance — so it cannot reach the
 * context's `topUpCredits`, and a second hand-written Razorpay flow is how the
 * confirm-payment step gets forgotten in one of them: the order is charged, the
 * server never hears about it, and the balance silently stays put.
 *
 * Deliberately transport-only. It does not touch React state; the caller owns
 * what to do with the new balance.
 */

function loadRazorpayCheckout() {
    if (window.Razorpay) return Promise.resolve();
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay')), { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Razorpay'));
        document.body.appendChild(script);
    });
}

/**
 * Order → Razorpay checkout → confirm. Resolves with the confirm-payment body
 * (`{ credits, data: { balanceAfter } }`); rejects with `Payment cancelled`
 * when the customer dismisses the sheet, so callers can tell that apart from a
 * genuine failure.
 */
export async function purchaseCredits(creditsToBuy) {
    const creditCount = Math.ceil(Number(creditsToBuy));
    if (!Number.isFinite(creditCount) || creditCount <= 0) {
        throw new Error('Enter a valid credit amount');
    }

    const { data: orderResponse } = await api.post('/settings/credits/create-order', {
        credits: creditCount,
    });
    const order = orderResponse.data;

    await loadRazorpayCheckout();

    return new Promise((resolve, reject) => {
        const checkout = new window.Razorpay({
            key: order.razorpayKeyId,
            amount: order.amountMinorUnits,
            currency: order.currency,
            name: 'Cognitive Flows',
            description: `${order.credits} credits`,
            order_id: order.orderId,
            handler: async (response) => {
                try {
                    const { data } = await api.post('/settings/credits/confirm-payment', {
                        orderId: order.orderId,
                        paymentId: response.razorpay_payment_id,
                    });
                    resolve(data);
                } catch (err) {
                    reject(err);
                }
            },
            modal: {
                ondismiss: () => reject(new Error('Payment cancelled')),
            },
        });
        checkout.open();
    });
}

export default purchaseCredits;
